import Quill from 'quill';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import 'quill/dist/quill.snow.css';

interface HookRichTextEditorProps {
  name: string;
  height?: string;
}

const HookRichTextEditor: React.FC<HookRichTextEditorProps> = ({ name, height = '200px' }) => {
  const { control } = useFormContext();
  const quillRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const quillInstance = useRef<Quill | null>(null);
  const onChangeRef = useRef<any>(null);
  const initialValueRef = useRef<any>(null);

  useEffect(() => {
    if (quillRef.current && toolbarRef.current && !quillInstance.current) {
      quillInstance.current = new Quill(quillRef.current, {
        theme: 'snow',
        modules: {
          toolbar: toolbarRef.current,
        },
      });

      const quill = quillInstance.current;
      if (initialValueRef.current) {
        quill.clipboard.dangerouslyPasteHTML(initialValueRef.current);
      }

      quill.on('text-change', (_delta, _oldDelta, source) => {
        if (source === 'user') {
          onChangeRef.current(quill.root.innerHTML);
        }
      });
    }

    return () => {
      quillInstance.current = null;
    };
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const { onChange, value } = field;
        onChangeRef.current = onChange;
        initialValueRef.current = value;

        return (
          <div className='text-editor'>
            <div ref={toolbarRef}>
              <span className='ql-formats'>
                <button className='ql-bold' />
                <button className='ql-italic' />
                <button className='ql-underline' />
                <button className='ql-strike' />
              </span>
              <span className='ql-formats'>
                <button className='ql-list' value='ordered' />
                <button className='ql-list' value='bullet' />
                <button className='ql-indent' value='-1' />
                <button className='ql-indent' value='+1' />
              </span>
              <span className='ql-formats'>
                <button className='ql-link' />
                <button className='ql-image' />
              </span>
              <span className='ql-formats'>
                <button className='ql-clean' />
              </span>
            </div>
            <div ref={quillRef} style={{ height }} />
          </div>
        );
      }}
    />
  );
};

export default HookRichTextEditor;
